﻿package org.zq.pixelset.processing

import org.zq.pixelset.model.DecodedImage
import org.zq.pixelset.model.EditParams
import org.zq.pixelset.model.NormalizedRect

object ImageTransforms {

    fun applyEdits(source: DecodedImage, params: EditParams): DecodedImage {
        var image = source
        if (params.rotationDegrees != 0) {
            image = rotate(image, params.rotationDegrees)
        }
        if (params.hasVisualCrop) {
            image = cropByRect(image, params.visualCrop)
        } else if (params.hasMarginCrop) {
            image = cropByMargins(image, params.cropLeft, params.cropTop, params.cropRight, params.cropBottom)
        }
        if (params.hasFilters) {
            image = applyFilters(image, params.brightness, params.contrast, params.saturation)
        }
        return image
    }

    fun rotate(image: DecodedImage, degrees: Int): DecodedImage = when (degrees % 360) {
        90 -> rotate90Clockwise(image)
        180 -> rotate180(image)
        270 -> rotate90CounterClockwise(image)
        else -> image
    }

    private fun rotate90Clockwise(image: DecodedImage): DecodedImage {
        val w = image.width
        val h = image.height
        val out = IntArray(w * h)
        for (y in 0 until h) {
            for (x in 0 until w) {
                out[x * h + (h - 1 - y)] = image.pixels[y * w + x]
            }
        }
        return DecodedImage(h, w, out)
    }

    private fun rotate90CounterClockwise(image: DecodedImage): DecodedImage {
        val w = image.width
        val h = image.height
        val out = IntArray(w * h)
        for (y in 0 until h) {
            for (x in 0 until w) {
                out[(w - 1 - x) * h + y] = image.pixels[y * w + x]
            }
        }
        return DecodedImage(h, w, out)
    }

    private fun rotate180(image: DecodedImage): DecodedImage {
        val out = IntArray(image.pixels.size)
        val last = image.pixels.lastIndex
        for (i in image.pixels.indices) {
            out[last - i] = image.pixels[i]
        }
        return DecodedImage(image.width, image.height, out)
    }

    fun cropByRect(image: DecodedImage, rect: NormalizedRect): DecodedImage {
        val r = rect.clamped()
        val l = (image.width * r.left).toInt().coerceIn(0, image.width - 1)
        val t = (image.height * r.top).toInt().coerceIn(0, image.height - 1)
        val rEdge = (image.width * r.right).toInt().coerceIn(l + 1, image.width)
        val bEdge = (image.height * r.bottom).toInt().coerceIn(t + 1, image.height)
        val w = rEdge - l
        val h = bEdge - t
        val pixels = IntArray(w * h)
        for (y in 0 until h) {
            for (x in 0 until w) {
                pixels[y * w + x] = image.pixels[(t + y) * image.width + (l + x)]
            }
        }
        return DecodedImage(w, h, pixels)
    }

    fun cropByMargins(
        image: DecodedImage,
        left: Float,
        top: Float,
        right: Float,
        bottom: Float,
    ): DecodedImage {
        val maxMargin = 0.45f
        val lFrac = left.coerceIn(0f, maxMargin)
        val tFrac = top.coerceIn(0f, maxMargin)
        val rFrac = right.coerceIn(0f, maxMargin)
        val bFrac = bottom.coerceIn(0f, maxMargin)
        val l = (image.width * lFrac).toInt()
        val t = (image.height * tFrac).toInt()
        val r = (image.width * rFrac).toInt()
        val b = (image.height * bFrac).toInt()
        val w = image.width - l - r
        val h = image.height - t - b
        if (w < 1 || h < 1) return image
        val pixels = IntArray(w * h)
        for (y in 0 until h) {
            for (x in 0 until w) {
                pixels[y * w + x] = image.pixels[(t + y) * image.width + (l + x)]
            }
        }
        return DecodedImage(w, h, pixels)
    }

    fun applyFilters(
        image: DecodedImage,
        brightness: Int,
        contrast: Float,
        saturation: Float,
    ): DecodedImage {
        val out = image.pixels.copyOf()
        val bAdj = brightness.coerceIn(-100, 100)
        val cAdj = contrast.coerceIn(0.5f, 2f)
        val sAdj = saturation.coerceIn(0f, 2f)
        for (i in out.indices) {
            val a = (out[i] shr 24) and 0xFF
            var r = (out[i] shr 16) and 0xFF
            var g = (out[i] shr 8) and 0xFF
            var b = out[i] and 0xFF
            r = (r + bAdj).coerceIn(0, 255)
            g = (g + bAdj).coerceIn(0, 255)
            b = (b + bAdj).coerceIn(0, 255)
            r = ((r - 128) * cAdj + 128).toInt().coerceIn(0, 255)
            g = ((g - 128) * cAdj + 128).toInt().coerceIn(0, 255)
            b = ((b - 128) * cAdj + 128).toInt().coerceIn(0, 255)
            val gray = 0.299 * r + 0.587 * g + 0.114 * b
            r = (gray + (r - gray) * sAdj).toInt().coerceIn(0, 255)
            g = (gray + (g - gray) * sAdj).toInt().coerceIn(0, 255)
            b = (gray + (b - gray) * sAdj).toInt().coerceIn(0, 255)
            out[i] = (a shl 24) or (r shl 16) or (g shl 8) or b
        }
        return DecodedImage(image.width, image.height, out)
    }
}
